import { supabase } from "../../Services/supabaseClient";

export const getAccounts = async (companyId) => {
  const { data, error } = await supabase
    .from("crm_accounts")
    .select("*")
    .eq("company_id", companyId)
    .order("name", { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
};

export const createAccount = async (payload) => {
  const { data, error } = await supabase
    .from("crm_accounts")
    .insert([payload])
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
};

export const updateAccount = async (accountId, companyId, payload) => {
  const { data, error } = await supabase
    .from("crm_accounts")
    .update(payload)
    .eq("id", accountId)
    .eq("company_id", companyId)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
};

export const getAccount360 = async (companyId, accountId) => {
  const { data: account, error: accountError } = await supabase
    .from("crm_accounts")
    .select("*")
    .eq("company_id", companyId)
    .eq("id", accountId)
    .single();

  if (accountError) {
    throw accountError;
  }

  const [
    contactsRes,
    opportunitiesRes,
    quotesRes,
    activitiesRes,
    collectionsRes,
    invoicesRes,
    paymentsRes,
  ] = await Promise.all([
    supabase
      .from("crm_contacts")
      .select("*")
      .eq("company_id", companyId)
      .eq("account_id", accountId)
      .order("is_primary", { ascending: false }),
    supabase
      .from("crm_opportunities")
      .select(
        `
        *,
        crm_pipeline_stages(id, name, position)
      `
      )
      .eq("company_id", companyId)
      .eq("account_id", accountId)
      .order("created_at", { ascending: false }),
    supabase
      .from("crm_quotes")
      .select("*")
      .eq("company_id", companyId)
      .eq("account_id", accountId)
      .order("created_at", { ascending: false }),
    supabase
      .from("crm_activities")
      .select("*")
      .eq("company_id", companyId)
      .eq("related_type", "account")
      .eq("related_id", accountId)
      .order("created_at", { ascending: false }),
    supabase
      .from("crm_collections")
      .select("*")
      .eq("company_id", companyId)
      .eq("account_id", accountId)
      .order("created_at", { ascending: false }),
    supabase
      .from("invoices")
      .select("*")
      .eq("company_id", companyId)
      .eq("account_id", accountId)
      .order("created_at", { ascending: false }),
    supabase
      .from("payments")
      .select("*")
      .eq("company_id", companyId)
      .eq("account_id", accountId)
      .order("created_at", { ascending: false }),
  ]);

  if (contactsRes.error) throw contactsRes.error;
  if (opportunitiesRes.error) throw opportunitiesRes.error;
  if (quotesRes.error) throw quotesRes.error;
  if (activitiesRes.error) throw activitiesRes.error;
  if (collectionsRes.error) throw collectionsRes.error;
  if (invoicesRes.error) throw invoicesRes.error;
  if (paymentsRes.error) throw paymentsRes.error;

  const contacts = contactsRes.data || [];
  const opportunities = opportunitiesRes.data || [];
  const quotes = quotesRes.data || [];
  const activities = activitiesRes.data || [];
  const collections = collectionsRes.data || [];
  const invoices = invoicesRes.data || [];
  const payments = paymentsRes.data || [];

  const outstandingReceivables = invoices.reduce((sum, row) => {
    const totalAmount = Number(row.total_amount || row.total || 0);
    const amountPaid = Number(row.amount_paid || 0);
    return sum + Math.max(totalAmount - amountPaid, 0);
  }, 0);

  const totalPayments = payments.reduce((sum, row) => {
    return sum + Number(row.amount || 0);
  }, 0);

  return {
    account,
    contacts,
    opportunities,
    quotes,
    activities,
    collections,
    invoices,
    payments,
    summary: {
      openOpportunities: opportunities.filter((row) => row.status === "open")
        .length,
      openQuotes: quotes.filter(
        (row) => !["converted", "rejected"].includes(row.status)
      ).length,
      outstandingReceivables,
      totalPayments,
      openCollections: collections.filter((row) => row.status !== "resolved")
        .length,
    },
  };
};
